import { useEffect, useState } from "react";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { partnersApi, type PartnerWithProjects } from "@services/api/partners";
import { showNotification } from "context/notification.service";

export default function ClientArtisansScreen() {
  const [partners, setPartners] = useState<PartnerWithProjects[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await partnersApi.listForGuestWithProjects();
        if (alive) setPartners(data);
      } catch {
        if (alive) {
          showNotification({
            type: "error",
            message: "Impossible de charger la liste des artisans",
          });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col w-full max-w-1p2k">
          <Text1>Artisans</Text1>
          <Text2>
            Liste des artisans intervenant sur vos projets, avec leurs
            coordonnées de contact.
          </Text2>
        </div>
      </div>
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col w-full">
        <div className="flex-1 min-h-0 max-w-1p2k w-full overflow-auto rounded-xl border-1 border-neutral-200 bg-white">
          <table className="w-full text-h3">
            <thead className="bg-neutral-50 sticky top-0 z-10">
              <tr className="text-left text-neutral-500">
                <th className="px-4 py-3 font-medium">Artisan</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Projet(s)</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="px-4 py-6 text-neutral-400" colSpan={3}>
                    Chargement…
                  </td>
                </tr>
              )}
              {!loading && partners.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-neutral-400" colSpan={3}>
                    Aucun artisan associé à vos projets pour le moment.
                  </td>
                </tr>
              )}
              {!loading &&
                partners.map((p) => (
                  <tr
                    key={p.id}
                    className="border-t-1 border-neutral-100 align-top"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-neutral-800">
                        {p.company_name || p.display_name || "—"}
                      </div>
                      {p.company_name && p.display_name && (
                        <div className="text-h4 text-neutral-500">
                          {p.display_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {p.email && (
                          <a
                            href={`mailto:${p.email}`}
                            className="text-neutral-700 hover:underline"
                          >
                            {p.email}
                          </a>
                        )}
                        {!p.email && p.company_email && (
                          <a
                            href={`mailto:${p.company_email}`}
                            className="text-neutral-700 hover:underline"
                          >
                            {p.company_email}
                          </a>
                        )}
                        {p.phone_number && (
                          <a
                            href={`tel:${p.phone_number}`}
                            className="text-neutral-700 hover:underline"
                          >
                            {p.phone_number}
                          </a>
                        )}
                        {!p.phone_number && p.company_phone && (
                          <a
                            href={`tel:${p.company_phone}`}
                            className="text-neutral-700 hover:underline"
                          >
                            {p.company_phone}
                          </a>
                        )}
                        {!p.email &&
                          !p.company_email &&
                          !p.phone_number &&
                          !p.company_phone && (
                            <span className="text-neutral-400">—</span>
                          )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {p.projects.map((proj) => (
                          <span key={proj.id} className="text-neutral-700">
                            {proj.name || proj.num || proj.id}
                            {proj.num && proj.name ? (
                              <span className="text-neutral-400">
                                {" "}
                                · {proj.num}
                              </span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
